import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningIcon from "@mui/icons-material/Warning";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { PageHeader } from "./ui";

const OpsAlerts = ({ onAlertsCountChange, alertsData }) => {
  const theme = useTheme();
  const [expandedTargets, setExpandedTargets] = useState(() => {
    const savedExpandedTargets = localStorage.getItem(
      "opsAlertsExpandedTargets",
    );
    return savedExpandedTargets ? JSON.parse(savedExpandedTargets) : {};
  });

  const [expandedSections, setExpandedSections] = useState(() => {
    const savedExpandedSections = localStorage.getItem(
      "opsAlertsExpandedSections",
    );
    return savedExpandedSections
      ? JSON.parse(savedExpandedSections)
      : {
          stockRatio: false,
          banksGIP: false,
          a144AinGIP: false,
          regS: false,
          shortTags: false,
          lpPositions: false,
          boxPositions: false,
          convertArb: false,
        };
  });

  const checkAndClearStorageIfNewDay = () => {
    const lastVisitDate = localStorage.getItem("opsAlertsLastVisitDate");
    const currentDate = new Date().toDateString();

    if (lastVisitDate !== currentDate) {
      localStorage.removeItem("opsAlertsExpandedTargets");
      localStorage.removeItem("opsAlertsExpandedSections");
      localStorage.removeItem("opsAlertsScrollPosition");
      localStorage.setItem("opsAlertsLastVisitDate", currentDate);
    }
  };

  useEffect(() => {
    checkAndClearStorageIfNewDay();
  }, []);

  useEffect(() => {
    localStorage.setItem(
      "opsAlertsExpandedTargets",
      JSON.stringify(expandedTargets),
    );
  }, [expandedTargets]);

  useEffect(() => {
    localStorage.setItem(
      "opsAlertsExpandedSections",
      JSON.stringify(expandedSections),
    );
  }, [expandedSections]);

  useEffect(() => {
    // Calculate and update alerts count whenever alertsData changes
    const totalAlerts = getTotalAlerts();
    if (onAlertsCountChange) {
      onAlertsCountChange(totalAlerts);
    }
  }, [alertsData, onAlertsCountChange]);

  useEffect(() => {
    // Restore scroll position after component mounts
    const savedScrollPosition = localStorage.getItem("opsAlertsScrollPosition");
    if (savedScrollPosition) {
      window.scrollTo(0, parseInt(savedScrollPosition));
    }

    // Save scroll position before unload
    const handleBeforeUnload = () => {
      localStorage.setItem(
        "opsAlertsScrollPosition",
        window.scrollY.toString(),
      );
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const handleTargetExpand = (target) => {
    setExpandedTargets((prev) => ({
      ...prev,
      [target]: !prev[target],
    }));
  };

  const handleSectionExpand = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const getGroupedData = () => {
    if (!alertsData.stock_ratio_check) return {};
    const grouped = alertsData.stock_ratio_check.reduce((acc, item) => {
      if (!acc[item.Target]) {
        acc[item.Target] = [];
      }
      acc[item.Target].push(item);
      return acc;
    }, {});

    // Sort the targets alphabetically
    const sortedGrouped = {};
    Object.keys(grouped)
      .sort((a, b) => a.localeCompare(b))
      .forEach((key) => {
        sortedGrouped[key] = grouped[key];
      });

    return sortedGrouped;
  };

  const getGroupedShortTags = () => {
    if (!alertsData.short_tags) return [];
    const grouped = alertsData.short_tags.reduce((acc, item) => {
      const key = `${item.StrategyType}-${item.Strategy1}-${item.Symbol}-${item.Alias2}-${item.SecType}-${item.isHedge}`;
      if (!acc[key]) {
        acc[key] = {
          StrategyType: item.StrategyType,
          Strategy1: item.Strategy1,
          Symbol: item.Symbol,
          Alias2: item.Alias2,
          SecType: item.SecType,
          isHedge: item.isHedge,
          Isin: item.Isin,
          Comment: item.Comment,
          MissingComment: item.MissingComment,
          Amount: 0,
        };
      }
      acc[key].Amount += item.Amount;
      return acc;
    }, {});

    return Object.values(grouped);
  };

  const getMissingCommentCount = () => {
    const groupedTags = getGroupedShortTags();
    return groupedTags?.filter((item) => !item.Comment || item.Comment === "")
      ?.length;
  };

  const formatNumber = (value) => {
    if (value < 0) {
      return `(${Math.abs(value).toLocaleString()})`;
    }
    return value.toLocaleString();
  };

  const getNumberColor = (value) => {
    if (value < 0) {
      return "error.main";
    }
    return "success.main";
  };

  const getTotalAlerts = () => {
    return (
      (alertsData.banks_in_GIP?.length || 0) +
      (alertsData["144A_in_GIP"]?.length || 0) +
      (alertsData.REG_S_in_GEF?.length || 0) +
      getMissingCommentCount() +
      (alertsData.LP_positions?.length || 0) +
      (alertsData.convert_arb_positions?.length || 0)
    );
  };

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          backgroundColor: theme.palette.background.paper,
          borderRadius: 0,
          boxShadow: theme.shadows[1],
        }}
      >
        <PageHeader
          sx={{
            px: "20px",
            justifyContent: "flex-start",
            alignItems: "end",
          }}
          title={"Ops Alerts Dashboard"}
        />
        <Typography variant="body2" color="text.secondary" sx={{ px: "20px" }}>
          Last Updated: {alertsData.last_updated}
        </Typography>
      </Box>

      {/* Stock Ratio Check Alert */}
      <Accordion
        expanded={expandedSections.stockRatio}
        onChange={() => handleSectionExpand("stockRatio")}
        sx={{
          mb: 2,
          "&:before": { display: "none" },
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          borderRadius: "8px !important",
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="stock-ratio-content"
          id="stock-ratio-header"
          sx={{
            backgroundColor: "rgba(211, 47, 47, 0.08)",
            borderLeft: "4px solid #d32f2f",
            "&:hover": {
              backgroundColor: "rgba(211, 47, 47, 0.12)",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <WarningIcon sx={{ color: "error.main", mr: 2 }} />
            <Typography sx={{ fontWeight: 600 }}>Stock Ratio Check</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width="40px" />
                  <TableCell width="120px">
                    <Typography sx={{ fontWeight: 600 }}>Target</Typography>
                  </TableCell>
                  <TableCell align="right" width="100px">
                    <Typography sx={{ fontWeight: 600 }}>
                      Target Position
                    </Typography>
                  </TableCell>
                  <TableCell align="right" width="100px">
                    <Typography sx={{ fontWeight: 600 }}>
                      Buyer Position
                    </Typography>
                  </TableCell>
                  <TableCell align="right" width="100px">
                    <Typography sx={{ fontWeight: 600 }}>
                      Required Hedge
                    </Typography>
                  </TableCell>
                  <TableCell align="right" width="100px">
                    <Typography sx={{ fontWeight: 600 }}>Difference</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(getGroupedData())?.map(([target, items]) => {
                  const totalTarget = items.reduce(
                    (sum, item) => sum + item["Target Position"],
                    0,
                  );
                  const totalBuyer = items.reduce(
                    (sum, item) => sum + item["Buyer Postion"],
                    0,
                  );
                  const totalRequiredHedge = items.reduce(
                    (sum, item) => sum + item["Required_Hedge"],
                    0,
                  );
                  const totalDiff = items.reduce(
                    (sum, item) => sum + item.Differance,
                    0,
                  );

                  return (
                    <React.Fragment key={target}>
                      <TableRow
                        sx={{
                          "& > *": { borderBottom: "unset" },
                          cursor: "pointer",
                          "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                        }}
                        onClick={() => handleTargetExpand(target)}
                      >
                        <TableCell>
                          <IconButton
                            aria-label="expand row"
                            size="small"
                            sx={{ p: 0 }}
                          >
                            {expandedTargets[target] ? (
                              <KeyboardArrowUpIcon />
                            ) : (
                              <KeyboardArrowDownIcon />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell component="th" scope="row">
                          <Typography
                            sx={{ fontWeight: 500, color: "primary.main" }}
                          >
                            {target}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            sx={{
                              color: getNumberColor(totalTarget),
                              fontWeight: 500,
                            }}
                          >
                            {formatNumber(totalTarget)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            sx={{
                              color: getNumberColor(totalBuyer),
                              fontWeight: 500,
                            }}
                          >
                            {formatNumber(totalBuyer)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            sx={{
                              color: getNumberColor(totalRequiredHedge),
                              fontWeight: 500,
                            }}
                          >
                            {formatNumber(totalRequiredHedge)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            sx={{
                              color: getNumberColor(totalDiff),
                              fontWeight: 500,
                            }}
                          >
                            {formatNumber(totalDiff)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          style={{ paddingBottom: 0, paddingTop: 0 }}
                          colSpan={6}
                        >
                          <Collapse
                            in={expandedTargets[target]}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box sx={{ margin: 0 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell width="120px">
                                      <Typography sx={{ fontWeight: 600 }}>
                                        Buyer
                                      </Typography>
                                    </TableCell>
                                    <TableCell width="100px">
                                      <Typography sx={{ fontWeight: 600 }}>
                                        Portfolio
                                      </Typography>
                                    </TableCell>
                                    <TableCell width="80px">
                                      <Typography sx={{ fontWeight: 600 }}>
                                        Stock Ratio
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right" width="100px">
                                      <Typography sx={{ fontWeight: 600 }}>
                                        Target Position
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right" width="100px">
                                      <Typography sx={{ fontWeight: 600 }}>
                                        Buyer Position
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right" width="100px">
                                      <Typography sx={{ fontWeight: 600 }}>
                                        Required Hedge
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right" width="100px">
                                      <Typography sx={{ fontWeight: 600 }}>
                                        Difference
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {items?.map((item, index) => (
                                    <TableRow
                                      key={index}
                                      sx={{
                                        "&:last-child td, &:last-child th": {
                                          border: 0,
                                        },
                                        "&:hover": {
                                          backgroundColor:
                                            "rgba(0, 0, 0, 0.04)",
                                          cursor: "pointer",
                                        },
                                      }}
                                    >
                                      <TableCell>{item.Buyer}</TableCell>
                                      <TableCell>{item.Portfolio}</TableCell>
                                      <TableCell>
                                        {item["Stock Ratio"]}
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography
                                          sx={{
                                            color: getNumberColor(
                                              item["Target Position"],
                                            ),
                                            fontWeight: 500,
                                          }}
                                        >
                                          {formatNumber(
                                            item["Target Position"],
                                          )}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography
                                          sx={{
                                            color: getNumberColor(
                                              item["Buyer Postion"],
                                            ),
                                            fontWeight: 500,
                                          }}
                                        >
                                          {formatNumber(item["Buyer Postion"])}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography
                                          sx={{
                                            color: getNumberColor(
                                              item["Required_Hedge"],
                                            ),
                                            fontWeight: 500,
                                          }}
                                        >
                                          {formatNumber(item["Required_Hedge"])}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography
                                          sx={{
                                            color: getNumberColor(
                                              item.Differance,
                                            ),
                                            fontWeight: 500,
                                          }}
                                        >
                                          {formatNumber(item.Differance)}
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Banks in GIP Alert */}
      <Accordion
        expanded={expandedSections.banksGIP}
        onChange={() => handleSectionExpand("banksGIP")}
        sx={{
          mb: 2,
          "&:before": { display: "none" },
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          borderRadius: "8px !important",
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="banks-gip-content"
          id="banks-gip-header"
          sx={{
            backgroundColor: "rgba(156, 39, 176, 0.08)",
            borderLeft: "4px solid #9c27b0",
            "&:hover": {
              backgroundColor: "rgba(156, 39, 176, 0.12)",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <WarningIcon sx={{ color: "error.main", mr: 2 }} />
            <Typography sx={{ fontWeight: 600 }}>Banks in GIP</Typography>
            <Chip
              label={(alertsData.banks_in_GIP || [])?.length || 0}
              size="small"
              color="error"
              sx={{
                fontWeight: "bold",
                minWidth: "32px",
                ml: 1,
                height: "20px",
              }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer>
            <Table size="small" sx={{ "& .MuiTableCell-root": { py: 0.5 } }}>
              <TableHead>
                <TableRow>
                  <TableCell width="120px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      Security Name
                    </Typography>
                  </TableCell>
                  <TableCell width="120px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      Strategy Type
                    </Typography>
                  </TableCell>
                  <TableCell width="120px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>Strategy</Typography>
                  </TableCell>
                  <TableCell width="100px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>Symbol</Typography>
                  </TableCell>
                  <TableCell width="100px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>ISIN</Typography>
                  </TableCell>
                  <TableCell width="120px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>Alias</Typography>
                  </TableCell>
                  <TableCell width="100px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>Sec Type</Typography>
                  </TableCell>
                  <TableCell width="120px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>Industry</Typography>
                  </TableCell>
                  <TableCell align="right" width="100px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>Position</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(alertsData.banks_in_GIP || [])?.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                        cursor: "pointer",
                      },
                    }}
                  >
                    <TableCell>{item.Security_Name}</TableCell>
                    <TableCell>{item.StrategyType}</TableCell>
                    <TableCell>{item.Strategy1}</TableCell>
                    <TableCell>{item.Symbol}</TableCell>
                    <TableCell>{item.Isin}</TableCell>
                    <TableCell>{item.Alias2}</TableCell>
                    <TableCell>{item.SecType}</TableCell>
                    <TableCell>{item.IndustryDesc}</TableCell>
                    <TableCell align="right">
                      <Typography
                        sx={{
                          color: getNumberColor(item.Amount),
                          fontWeight: 500,
                        }}
                      >
                        {formatNumber(item.Amount)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* REG S in GEF Alert */}
      <Accordion
        expanded={expandedSections.regS}
        onChange={() => handleSectionExpand("regS")}
        sx={{
          mb: 2,
          "&:before": { display: "none" },
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          borderRadius: "8px !important",
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="reg-s-content"
          id="reg-s-header"
          sx={{
            backgroundColor: "rgba(233, 30, 99, 0.08)",
            borderLeft: "4px solid #e91e63",
            "&:hover": {
              backgroundColor: "rgba(233, 30, 99, 0.12)",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <WarningIcon sx={{ color: "error.main", mr: 2 }} />
            <Typography sx={{ fontWeight: 600 }}>REG S in GEF</Typography>
            <Chip
              label={(alertsData.REG_S_in_GEF || [])?.length || 0}
              size="small"
              color="error"
              sx={{
                fontWeight: "bold",
                minWidth: "32px",
                ml: 1,
                height: "20px",
              }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer>
            <Table size="small" sx={{ "& .MuiTableCell-root": { py: 0.5 } }}>
              <TableHead>
                <TableRow>
                  <TableCell width="120px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      Security Name
                    </Typography>
                  </TableCell>
                  <TableCell width="120px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      Strategy Type
                    </Typography>
                  </TableCell>
                  <TableCell width="120px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>Strategy</Typography>
                  </TableCell>
                  <TableCell width="100px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>Symbol</Typography>
                  </TableCell>
                  <TableCell width="100px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>ISIN</Typography>
                  </TableCell>
                  <TableCell width="120px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>Alias</Typography>
                  </TableCell>
                  <TableCell width="100px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>Sec Type</Typography>
                  </TableCell>
                  <TableCell width="80px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>REG S</Typography>
                  </TableCell>
                  <TableCell width="80px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>144A</Typography>
                  </TableCell>
                  <TableCell align="right" width="100px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>Position</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(alertsData.REG_S_in_GEF || [])?.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                        cursor: "pointer",
                      },
                    }}
                  >
                    <TableCell>{item.Security_Name}</TableCell>
                    <TableCell>{item.StrategyType}</TableCell>
                    <TableCell>{item.Strategy1}</TableCell>
                    <TableCell>{item.Symbol}</TableCell>
                    <TableCell>{item.Isin}</TableCell>
                    <TableCell>{item.Alias2}</TableCell>
                    <TableCell>{item.SecType}</TableCell>
                    <TableCell>{item["REF S Flag"]}</TableCell>
                    <TableCell>{item["144A Flag"]}</TableCell>
                    <TableCell align="right">
                      <Typography
                        sx={{
                          color: getNumberColor(item.Amount),
                          fontWeight: 500,
                        }}
                      >
                        {formatNumber(item.Amount)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Short Positions without Tags Alert */}
      <Accordion
        expanded={expandedSections.shortTags}
        onChange={() => handleSectionExpand("shortTags")}
        sx={{
          mb: 2,
          "&:before": { display: "none" },
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          borderRadius: "8px !important",
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="short-tags-content"
          id="short-tags-header"
          sx={{
            backgroundColor: "rgba(0, 188, 212, 0.08)",
            borderLeft: "4px solid #00bcd4",
            "&:hover": {
              backgroundColor: "rgba(0, 188, 212, 0.12)",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <WarningIcon sx={{ color: "error.main", mr: 2 }} />
            <Typography sx={{ fontWeight: 600 }}>
              Short Positions without Tags/Comments
            </Typography>
            <Chip
              label={getMissingCommentCount()}
              size="small"
              color="error"
              sx={{
                fontWeight: "bold",
                minWidth: "32px",
                ml: 1,
                height: "20px",
              }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer>
            <Table size="small" sx={{ "& .MuiTableCell-root": { py: 0.5 } }}>
              <TableHead>
                <TableRow>
                  <TableCell width="120px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      Strategy Type
                    </Typography>
                  </TableCell>
                  <TableCell width="120px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>Strategy</Typography>
                  </TableCell>
                  <TableCell width="100px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>Symbol</Typography>
                  </TableCell>
                  <TableCell width="100px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>ISIN</Typography>
                  </TableCell>
                  <TableCell width="120px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>Alias</Typography>
                  </TableCell>
                  <TableCell width="100px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>Sec Type</Typography>
                  </TableCell>
                  <TableCell width="80px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      EZE Hedge Tag
                    </Typography>
                  </TableCell>
                  <TableCell width="100px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      Comment by User
                    </Typography>
                  </TableCell>
                  <TableCell align="right" width="100px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      Total Position
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getGroupedShortTags()?.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                        cursor: "pointer",
                      },
                      backgroundColor: item.MissingComment
                        ? "rgba(255, 0, 0, 0.1)"
                        : "inherit",
                    }}
                  >
                    <TableCell>{item.StrategyType}</TableCell>
                    <TableCell>{item.Strategy1}</TableCell>
                    <TableCell>{item.Symbol}</TableCell>
                    <TableCell>{item.Isin}</TableCell>
                    <TableCell>{item.Alias2}</TableCell>
                    <TableCell>{item.SecType}</TableCell>
                    <TableCell>{item.isHedge}</TableCell>
                    <TableCell>{item.Comment}</TableCell>
                    <TableCell align="right">
                      <Typography
                        sx={{
                          color: getNumberColor(item.Amount),
                          fontWeight: 500,
                        }}
                      >
                        {formatNumber(item.Amount)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* LP Positions Alert */}
      <Accordion
        expanded={expandedSections.lpPositions}
        onChange={() => handleSectionExpand("lpPositions")}
        sx={{
          mb: 2,
          "&:before": { display: "none" },
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          borderRadius: "8px !important",
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="lp-positions-content"
          id="lp-positions-header"
          sx={{
            backgroundColor: "rgba(76, 175, 80, 0.08)",
            borderLeft: "4px solid #4caf50",
            "&:hover": {
              backgroundColor: "rgba(76, 175, 80, 0.12)",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <WarningIcon sx={{ color: "error.main", mr: 2 }} />
            <Typography sx={{ fontWeight: 600 }}>LP Positions</Typography>
            <Chip
              label={(alertsData.LP_positions || [])?.length || 0}
              size="small"
              color="error"
              sx={{
                fontWeight: "bold",
                minWidth: "32px",
                ml: 1,
                height: "20px",
              }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer>
            <Table size="small" sx={{ "& .MuiTableCell-root": { py: 0.5 } }}>
              <TableHead>
                <TableRow>
                  <TableCell width="100px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      Security Name
                    </Typography>
                  </TableCell>
                  <TableCell width="60px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      Strategy Type
                    </Typography>
                  </TableCell>
                  <TableCell width="60px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>Strategy</Typography>
                  </TableCell>
                  <TableCell width="50px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>Symbol</Typography>
                  </TableCell>
                  <TableCell width="100px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>ISIN</Typography>
                  </TableCell>
                  <TableCell width="120px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>Issuer</Typography>
                  </TableCell>
                  <TableCell width="100px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>Alias</Typography>
                  </TableCell>
                  <TableCell width="80px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>Sec Type</Typography>
                  </TableCell>
                  <TableCell width="60px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>SecType2</Typography>
                  </TableCell>
                  <TableCell align="right" width="80px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>Position</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(alertsData.LP_positions || [])?.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                        cursor: "pointer",
                      },
                    }}
                  >
                    <TableCell>{item.Security_Name}</TableCell>
                    <TableCell>{item.StrategyType}</TableCell>
                    <TableCell>{item.Strategy1}</TableCell>
                    <TableCell>{item.Symbol}</TableCell>
                    <TableCell>{item.Isin}</TableCell>
                    <TableCell>{item.Issuer}</TableCell>
                    <TableCell>{item.Alias2}</TableCell>
                    <TableCell>{item.SecType}</TableCell>
                    <TableCell>{item.SEC_TYPE_2}</TableCell>
                    <TableCell align="right">
                      <Typography
                        sx={{
                          color: getNumberColor(item.Amount),
                          fontWeight: 500,
                        }}
                      >
                        {formatNumber(item.Amount)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Boxed Positions Alert */}
      <Accordion
        expanded={expandedSections.boxPositions}
        onChange={() => handleSectionExpand("boxPositions")}
        sx={{
          mb: 2,
          "&:before": { display: "none" },
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          borderRadius: "8px !important",
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="box-positions-content"
          id="box-positions-header"
          sx={{
            backgroundColor: "rgba(255, 152, 0, 0.08)",
            borderLeft: "4px solid #ff9800",
            "&:hover": {
              backgroundColor: "rgba(255, 152, 0, 0.12)",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <WarningIcon sx={{ color: "error.main", mr: 2 }} />
            <Typography sx={{ fontWeight: 600 }}>Boxed Positions</Typography>
            <Chip
              label="Coming Soon"
              size="small"
              color="error"
              sx={{
                fontWeight: "bold",
                minWidth: "32px",
                ml: 1,
                height: "20px",
              }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Typography color="text.secondary">
              Box Positions alert section will be available soon.
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Convert Arb Positions Alert */}
      <Accordion
        expanded={expandedSections.convertArb}
        onChange={() => handleSectionExpand("convertArb")}
        sx={{
          mb: 2,
          "&:before": { display: "none" },
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          borderRadius: "8px !important",
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="convert-arb-content"
          id="convert-arb-header"
          sx={{
            backgroundColor: "rgba(33, 150, 243, 0.08)",
            borderLeft: "4px solid #2196f3",
            "&:hover": {
              backgroundColor: "rgba(33, 150, 243, 0.12)",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <WarningIcon sx={{ color: "error.main", mr: 2 }} />
            <Typography sx={{ fontWeight: 600 }}>
              Convert Arb Positions
            </Typography>
            <Chip
              label={(alertsData.convert_arb_positions || [])?.length || 0}
              size="small"
              color="error"
              sx={{
                fontWeight: "bold",
                minWidth: "32px",
                ml: 1,
                height: "20px",
              }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer>
            <Table size="small" sx={{ "& .MuiTableCell-root": { py: 0.5 } }}>
              <TableHead>
                <TableRow>
                  <TableCell width="120px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      Security Name
                    </Typography>
                  </TableCell>
                  <TableCell width="120px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      Strategy Type
                    </Typography>
                  </TableCell>
                  <TableCell width="120px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>Strategy</Typography>
                  </TableCell>
                  <TableCell width="100px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>Symbol</Typography>
                  </TableCell>
                  <TableCell width="100px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>ISIN</Typography>
                  </TableCell>
                  <TableCell width="120px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>Alias</Typography>
                  </TableCell>
                  <TableCell width="100px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>Sec Type</Typography>
                  </TableCell>
                  <TableCell align="right" width="100px" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>Position</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(alertsData.convert_arb_positions || [])?.map(
                  (item, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.04)",
                          cursor: "pointer",
                        },
                      }}
                    >
                      <TableCell>{item.Security_Name}</TableCell>
                      <TableCell>{item.StrategyType}</TableCell>
                      <TableCell>{item.Strategy1}</TableCell>
                      <TableCell>{item.Symbol}</TableCell>
                      <TableCell>{item.Isin}</TableCell>
                      <TableCell>{item.Alias2}</TableCell>
                      <TableCell>{item.SecType}</TableCell>
                      <TableCell align="right">
                        <Typography
                          sx={{
                            color: getNumberColor(item.Amount),
                            fontWeight: 500,
                          }}
                        >
                          {formatNumber(item.Amount)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ),
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default OpsAlerts;
